import React from 'react';
import useModalStore from 'store/modals';
import Modal from 'components/ui/modals';

// Raw Item Masters — self-contained (own their Modal)
import SetItem from 'components/item/SetItem';
import SetColor from 'components/color/SetColor';
import SetSize from 'components/size/SetSize';
import SetShape from 'components/shape/SetShape';
import SetUnit from 'components/unit/SetUnit';
import SetBrand from 'components/brand/SetBrand';
import SetSubBrand from 'components/subBrand/SetSubBrand';
import SetUOM from 'components/uom/SetUOM';
import SetArea from 'components/area/SetArea';
import SetSizeGrouping from 'components/sizeGrouping/SetSizeGrouping';

// Account Masters
import ManageGLAccountMastForm from 'components/ui/ManageGLAccountMastForm';
import SetGroup from 'components/groups/SetGroup';

// Other Masters
import SetProfilingMaster from 'components/profilingMaster/SetProfilingMaster';
import SetPricelist from 'components/pricelist/SetPricelist';
import SetCndnTypes from 'components/cndntypes/SetCndnTypes';
import SetVoucher from 'components/voucher/SetVoucher';
import SetMoneyReceipt from 'components/voucher/SetMoneyReceipt';
import SetCashBankVoucher from 'components/voucher/SetCashBankVoucher';
import PrintVoucher from 'components/voucher/PrintVoucher';
import SetSchemeGiftItem from 'components/schmitemaster/SetSchemeGiftItem';
import SetSchemeIssue from 'components/schIssue/SetSchemeIssue';

// Company Portal
import SetGodown from 'components/godown/SetGodown';
import SetUser from 'components/users/SetUser';

// Modal types that are now self-contained (own their <Modal>)
const SELF_CONTAINED_TYPES = new Set([
  'item', 'color', 'size', 'shape', 'unit', 'brand', 'subbrand', 'uom', 'area',
  'group', 'cndntypes', 'schmitemaster', 'godown', 'user',
  'profiling', 'pricelist', 'schissue', 'voucher', 'printVoucher',
  'sizeGrouping',
]);

const GlobalModalManager = () => {
  const { modals, closeModal, getModalZIndex } = useModalStore();

  const renderSelfContainedModal = (modalType, modalState) => {
    const onClose = () => closeModal(modalType);
    const { isOpen, editId, data: modalData } = modalState;

    switch (modalType) {
      case 'item':
        return <SetItem key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'color':
        return <SetColor key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'size':
        return <SetSize key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'shape':
        return <SetShape key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'unit':
        return <SetUnit key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'brand':
        return <SetBrand key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'subbrand':
        return <SetSubBrand key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'uom':
        return <SetUOM key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'area':
        return <SetArea key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'sizeGrouping':
        return <SetSizeGrouping key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'group':
        return <SetGroup key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'cndntypes':
        return <SetCndnTypes key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'schmitemaster':
        return <SetSchemeGiftItem key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'godown':
        return <SetGodown key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'user':
        return <SetUser key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'profiling':
        return <SetProfilingMaster key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'pricelist':
        return <SetPricelist key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'schissue':
        return <SetSchemeIssue key={modalType} open={isOpen} id={editId} onClose={onClose} />;
      case 'voucher':
        if (modalData?.type === 'MR') {
          return <SetMoneyReceipt key={modalType} open={isOpen} id={editId} onClose={onClose} />;
        }
        if (['BC', 'BD', 'CC', 'CD'].includes(modalData?.type)) {
          return <SetCashBankVoucher key={modalType} open={isOpen} id={editId} onClose={onClose} type={modalData.type} />;
        }
        return <SetVoucher key={modalType} open={isOpen} id={editId} onClose={onClose} type={modalData?.type} />;
      case 'printVoucher':
        return <PrintVoucher key={modalType} open={isOpen} voucher={modalData?.voucher} onClose={onClose} />;
      default:
        return null;
    }
  };

  const renderWrappedModalContent = (modalType, editId, onClose, modalData) => {
    switch (modalType) {
      case 'vendor':
        return (
          <ManageGLAccountMastForm
            ps_Id={editId}
            onCancel={onClose}
            onSubmit={onClose}
            initialData={{ agvct: modalData?.agvct || 'V' }}
          />
        );
      case 'customer':
        return (
          <ManageGLAccountMastForm
            ps_Id={editId}
            onCancel={onClose}
            onSubmit={onClose}
            initialData={{ agvct: 'C' }}
          />
        );
      case 'transporter':
        return (
          <ManageGLAccountMastForm
            ps_Id={editId}
            onCancel={onClose}
            onSubmit={onClose}
            initialData={{ agvct: 'T' }}
          />
        );
      default:
        return null;
    }
  };

  const getModalWidth = (modalType) => {
    switch (modalType) {
      case 'vendor':
      case 'customer':
      case 'transporter':
        return '2xl';
      default:
        return 'lg';
    }
  };

  return (
    <>
      {/* Self-contained modals — they own their own <Modal> wrapper */}
      {Object.entries(modals).map(([modalType, modalState]) => {
        if (!SELF_CONTAINED_TYPES.has(modalType)) return null;
        if (!modalState.isOpen) return null;
        return renderSelfContainedModal(modalType, modalState);
      })}

      {/* Legacy modals — wrapped here in <Modal> */}
      {Object.entries(modals).map(([modalType, modalState]) => {
        if (SELF_CONTAINED_TYPES.has(modalType)) return null;
        if (!modalState.isOpen) return null;

        return (
          <Modal
            key={modalType}
            open={modalState.isOpen}
            setOpen={(isOpen) => {
              if (!isOpen) closeModal(modalType);
            }}
            onClose={() => closeModal(modalType)}
            maxWidth={getModalWidth(modalType)}
            zIndex={getModalZIndex(modalType)}
          >
            {renderWrappedModalContent(
              modalType,
              modalState.editId,
              () => closeModal(modalType),
              modalState.data
            )}
          </Modal>
        );
      })}
    </>
  );
};

export default GlobalModalManager;
